/* ═══════════════════════════════════════════
   GOOGLE MAPS WIDGET — Embed de mapas
   Mantém o usuário no site ao invés de
   redirecionar para o Google Maps externo.
   ═══════════════════════════════════════════ */

import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';

export interface MapsWidgetProps {
  /** URL de embed do Google Maps (pb completo). Se fornecido, ignora query/lat/lng */
  src?: string;
  /** Query de busca no Google Maps (ex: "Ilha do Mel, Paranaguá, PR") */
  query?: string;
  /** Coordenadas latitude (usar com lng) */
  lat?: number;
  /** Coordenadas longitude (usar com lat) */
  lng?: number;
  /** Zoom do mapa (default: 14) */
  zoom?: number;
  /** Largura do embed (default: 100%) */
  width?: string;
  /** Altura do embed em pixels (default: 400) */
  height?: number;
  /** Título acessível para leitores de tela */
  title?: string;
  /** Estilo do mapa: roadmap, satellite, terrain (default: roadmap) */
  mapType?: 'roadmap' | 'satellite' | 'terrain';
}

export function MapsWidget({
  src: customSrc,
  query,
  lat,
  lng,
  zoom = 14,
  width = '100%',
  height = 400,
  title,
  mapType = 'roadmap',
}: MapsWidgetProps) {
  const { t } = useTranslation();
  const { trackOutbound } = useAnalytics();
  const resolvedTitle = title || t('widget.maps.title');

  let src: string;

  if (customSrc) {
    src = customSrc;
  } else if (lat !== undefined && lng !== undefined) {
    const params = new URLSearchParams({
      q: `${lat},${lng}`,
      z: String(zoom),
      t: mapType,
      output: 'embed',
    });
    src = `https://www.google.com/maps?${params.toString()}`;
  } else if (query) {
    const params = new URLSearchParams({
      q: query,
      z: String(zoom),
      t: mapType,
      output: 'embed',
    });
    src = `https://www.google.com/maps?${params.toString()}`;
  } else {
    throw new Error('MapsWidget: forneça "src", "query" ou "lat + lng"');
  }

  return (
    <div className="w-full rounded-2xl border border-sand-200 overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] bg-white">
      <div className="relative w-full" style={{ height }}>
        <iframe
          src={src}
          title={resolvedTitle}
          width={width}
          height={height}
          frameBorder={0}
          scrolling="no"
          loading="lazy"
          className="absolute inset-0 w-full h-full"
          allow="fullscreen"
        />
      </div>
      <div className="px-4 py-2.5 bg-sand-50 border-t border-sand-100 flex items-center justify-between">
        <span className="font-body text-[11px] text-warm-gray">{t('widget.maps.footer')}</span>
        <a
          href={customSrc || src.replace('&output=embed', '')}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackOutbound(customSrc || src.replace('&output=embed', ''), 'Abrir no Google Maps', 'widget_map')}
          className="font-body text-[11px] text-ocean hover:underline font-semibold"
        >
          {t('widget.maps.openMaps')}
        </a>
      </div>
    </div>
  );
}
